import { useCallback, useEffect, useState, type RefObject } from "react";
import { containTab, enterModalLayer, inertSiblings, restoreFocus } from "@mmm/ui";
const COLLAPSED_KEY = "mmm-sidebar-collapsed";
/** Keep in step with the mobile shell breakpoint in styles/sidebar.css. */
export const MOBILE_SHELL_MAX_WIDTH = 720;

const MOBILE_SHELL_QUERY = `(max-width: ${MOBILE_SHELL_MAX_WIDTH}px)`;

function matchesMobileShell(): boolean {
  return typeof window !== "undefined" && window.matchMedia(MOBILE_SHELL_QUERY).matches;
}

/** Marks the dismiss target, which must stay reachable while the drawer is open. */
export const BACKDROP_CLASS = "mmm-sidebar-backdrop";

export interface SidebarShell {
  /** Whether the rail renders in its narrow, icons-only form. */
  collapsed: boolean;
  /** Whether the sidebar is currently a modal drawer over the workspace. */
  asDrawer: boolean;
  /** Collapse (true) or expand (false) — a preference when wide, a gesture when narrow. */
  setCollapsed: (next: boolean) => void;
  closeDrawer: () => void;
}

/**
 * The sidebar is two different things either side of the breakpoint.
 *
 * Wide, it is a column the content shares a row with, and collapsing it is a
 * lasting preference worth storing.
 *
 * Narrow, it is a drawer: it holds a fixed 248px of a 390px phone, so it cannot
 * be a persistent column, and "open" is a transient act — dismissed by Escape,
 * by tapping away, or by going somewhere.
 *
 * Keeping those apart is the whole point. Persisting the narrow default as
 * though it were a choice was wrong twice over: an inferred value is not a
 * preference the user expressed, and a phone first opened in landscape would
 * store "expanded" and then honour it in portrait, covering the screen.
 */
export function useSidebarShell(
  /**
   * The router's identity for the current history entry, not the path. Closing
   * on a path change missed most of the ways you can navigate: clicking the
   * link for the route you are already on, a change of query string or hash,
   * and a redirect that lands on the same path all left the drawer sitting
   * over the destination. A location key changes on every navigation.
   */
  navigationKey: string,
  drawerRef: RefObject<HTMLElement | null>
): SidebarShell {
  const [preference, setPreference] = useState<boolean | null>(() => {
    const stored = localStorage.getItem(COLLAPSED_KEY);
    return stored === null ? null : stored === "1";
  });
  const [narrow, setNarrow] = useState(() => matchesMobileShell());
  // Remembering *where* it was opened is what closes it on navigation: the
  // drawer exists to take you somewhere, and going there must not leave it
  // sitting over the destination. Deriving that beats closing it in an effect,
  // which would render the new page with the drawer still up for a frame.
  const [openedAt, setOpenedAt] = useState<string | null>(null);
  const [seenKey, setSeenKey] = useState(navigationKey);

  // Forgetting the gesture outright on any navigation, rather than trusting the
  // key to stay unique. Location keys are reused when you go Back: the router
  // hands a POP navigation the same key the entry had on the way in, so an
  // openedAt left lying around from that entry matched again and the drawer
  // came back up on its own, several navigations later.
  //
  // Adjusting state during render rather than in an effect: React re-renders
  // before committing, so no frame is painted with the drawer still open.
  if (seenKey !== navigationKey) {
    setSeenKey(navigationKey);
    setOpenedAt(null);
  }

  const drawerOpen = openedAt !== null && openedAt === navigationKey;
  const collapsed = narrow ? !drawerOpen : (preference ?? false);
  const asDrawer = narrow && drawerOpen;

  // Rotating a phone, or dragging a window across the breakpoint, has to move
  // the sidebar with it — reading the viewport once at mount left a landscape
  // layout in place after rotating to portrait.
  useEffect(() => {
    const query = window.matchMedia(MOBILE_SHELL_QUERY);
    const onChange = (event: MediaQueryListEvent) => {
      setNarrow(event.matches);
      // Leaving drawer mode discards the gesture. Keeping it meant opening the
      // drawer in portrait, rotating to landscape, and rotating back brought
      // the old drawer up again, unasked, over whatever you were reading.
      if (!event.matches) setOpenedAt(null);
    };
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);

  const closeDrawer = useCallback(() => setOpenedAt(null), []);

  const setCollapsed = useCallback(
    (next: boolean) => {
      if (matchesMobileShell()) {
        // Opening the drawer is a gesture, not a preference — nothing is stored.
        setOpenedAt(next ? null : navigationKey);
        return;
      }
      setPreference(next);
      localStorage.setItem(COLLAPSED_KEY, next ? "1" : "0");
    },
    [navigationKey]
  );

  // A drawer that merely looks modal is worse than one that does not: the
  // dimmed workspace still took taps and scrolls, and Tab walked focus behind
  // it. Being modal has to hold for the keyboard and a screen reader too, not
  // just for a thumb -- `aria-modal` on a dialog whose siblings are still
  // reachable is a claim the page does not honour.
  useEffect(() => {
    if (!asDrawer) return;

    const drawer = drawerRef.current;
    if (!drawer) return;

    // The drawer is a dialog like any other, and has to take its turn. A node's
    // Delete opens a confirmation from inside the drawer without closing it, so
    // two dialogs are up: unguarded, the drawer answered the same Escape and
    // closed alongside the confirmation, and grabbed Tab first only to try to
    // move focus into its own contents, which the confirmation had made inert.
    const layer = enterModalLayer();
    const returnFocusTo = document.activeElement as HTMLElement | null;
    drawer.focus();
    // The backdrop is the dismiss target, so it is the one sibling that has to
    // stay reachable — inert it and there is no way out of the drawer by touch.
    const restoreSiblings = inertSiblings(drawer, (el) => el.classList.contains(BACKDROP_CLASS));

    const onKeyDown = (event: KeyboardEvent) => {
      if (!layer.isTop()) return;
      if (event.key === "Escape") {
        setOpenedAt(null);
        return;
      }
      if (event.key === "Tab") containTab(drawer, event);
    };

    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      layer.release();
      restoreSiblings();
      // The captured node, not a fresh ref read: by cleanup time the ref may
      // already point somewhere else, and the rail this drawer collapsed into
      // is the one focus should fall back to.
      restoreFocus(returnFocusTo, drawer);
    };
  }, [asDrawer, drawerRef]);

  return { collapsed, asDrawer, setCollapsed, closeDrawer };
}
